/* *******************************************************************
 * Copyright (c) 2025 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * *******************************************************************/

import { Injectable } from '@angular/core';
import { Logger } from '../utils/logger.util';
import { VCPM } from 'pcs-domain';

@Injectable({
  providedIn: 'root'
})
export class LegacyCalculatorCheckService {
  private cutoffDate!: Date;
  private esaCutoffDate!: Date;

  constructor() {
    this.loadCutoffDate();
    this.loadEsaCutoffDate();
  }

  /**
   * Load the legacy calculator cutoff date from ConfigMap
   */
  private loadCutoffDate(): void {
    try {
      const cutoffDateString = '${LEGACY_CALCULATOR_CUTOFF_DATE}';
      Logger.debug('[LegacyCalculatorCheck] Raw cutoff date string:', cutoffDateString);
      
      // Check if the placeholder has been replaced
      if (cutoffDateString && !cutoffDateString.startsWith('${')) {
        this.cutoffDate = new Date(cutoffDateString);
        Logger.debug('[LegacyCalculatorCheck] Cutoff date loaded from environment:', this.cutoffDate);
      } else {
        // Default to December 16, 2025 if not replaced (e.g., local development)
        this.cutoffDate = new Date('2025-12-16T00:00:00Z');
        Logger.debug('[LegacyCalculatorCheck] Using default cutoff date:', this.cutoffDate);
      }
    } catch (error) {
      this.cutoffDate = new Date('2025-12-16T00:00:00Z');
      Logger.error('[LegacyCalculatorCheck] Error loading cutoff date, using default:', error);
    }
  }

  /**
   * Load the ESA readynode cutoff date from ConfigMap
   */
  private loadEsaCutoffDate(): void {
    try {
      const esaCutoffDateString = '${ESA_READYNODE_CUTOFF_DATE}';
      Logger.debug('[LegacyCalculatorCheck] Raw ESA cutoff date string:', esaCutoffDateString);

      if (esaCutoffDateString && !esaCutoffDateString.startsWith('${')) {
        this.esaCutoffDate = new Date(esaCutoffDateString);
        Logger.debug('[LegacyCalculatorCheck] ESA cutoff date loaded from environment:', this.esaCutoffDate);
      } else {
        // Default to June 8, 2026 if not replaced (e.g., local development)
        this.esaCutoffDate = new Date('2026-06-08T00:00:00Z');
        Logger.debug('[LegacyCalculatorCheck] Using default ESA cutoff date:', this.esaCutoffDate);
      }
    } catch (error) {
      this.esaCutoffDate = new Date('2026-06-08T00:00:00Z');
      Logger.error('[LegacyCalculatorCheck] Error loading ESA cutoff date, using default:', error);
    }
  }

  /**
   * Check if a project uses the legacy calculator
   * Returns true if the project was created before the cutoff date
   * @param project The project to check
   * @returns true if project uses legacy calculator, false otherwise
   */
  isLegacyCalculatorProject(project: VCPM.Project | undefined): boolean {
    if (!project || !project.createdDate) {
      Logger.debug('[LegacyCalculatorCheck] Project or createdDate is undefined');
      return false;
    }

    try {
      const projectCreatedDate = new Date(project.createdDate);
      const isLegacy = projectCreatedDate < this.cutoffDate;
      
      Logger.debug('[LegacyCalculatorCheck] Project created:', projectCreatedDate, 'Cutoff:', this.cutoffDate, 'Is legacy:', isLegacy);
      
      return isLegacy;
    } catch (error) {
      Logger.error('[LegacyCalculatorCheck] Error checking project date:', error);
      return false;
    }
  }

  /**
   * Check if a 5.2.1 ESA project has stale readynode profile numbers.
   * Returns true only for projects created AFTER the legacy calculator cutoff
   * (pre-legacy projects are already covered by the legacy banner) and whose
   * last update is BEFORE the ESA readynode cutoff date.
   *
   * Using lastUpdate as the upper bound means the banner auto-dismisses as soon
   * as the user re-generates the recommendation (which saves the project and
   * stamps lastUpdate with the current time). Falls back to createdDate when
   * lastUpdate is null. These projects only need a recommendation re-generation
   * — no clone required.
   * @param project The project to check
   * @returns true if the project should show the ESA readynode alert
   */
  isStaleEsaProject(project: VCPM.Project | undefined): boolean {
    if (!project?.createdDate || project.metadata?.version !== '5.2.1') {
      return false;
    }

    const hasEsaWorkload = project.data?.workloadDomains?.some(domain =>
      domain.workloadList?.some(w =>
        w.storageConfiguration?.vsanArchitecture === 'ESA'
      )
    ) ?? false;

    if (!hasEsaWorkload) {
      return false;
    }

    try {
      const created = new Date(project.createdDate);

      // Skip projects already covered by the legacy banner (must clone first)
      if (created < this.cutoffDate) {
        return false;
      }

      // Use lastUpdate as the upper bound so the banner disappears automatically
      // once the user re-generates the recommendation. Fall back to createdDate
      // if the project has never been updated after creation.
      const compareDate = project.lastUpdate ? new Date(project.lastUpdate) : created;
      const isStaleEsa = compareDate < this.esaCutoffDate;
      Logger.debug('[LegacyCalculatorCheck] ESA check - lastUpdate:', compareDate, 'esaCutoff:', this.esaCutoffDate, 'isStaleEsa:', isStaleEsa);
      return isStaleEsa;
    } catch (error) {
      Logger.error('[LegacyCalculatorCheck] Error checking ESA project date:', error);
      return false;
    }
  }

  /**
   * Get the cutoff date for display purposes
   * @returns The cutoff date as a Date object
   */
  getCutoffDate(): Date {
    return this.cutoffDate;
  }

  /**
   * Get the cutoff date formatted as a string
   * @returns The cutoff date as a formatted string (e.g., "December 16, 2025")
   */
  getFormattedCutoffDate(): string {
    return this.cutoffDate.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  }
}

